import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../ComingSoon/_shared/AppIcon';
import { UpcomingHolidayView } from '../../../../models/attendance';
import { HolidayView } from '../../../../models/general';
import { appDate, appDayMonth, appMonthNameYear, appWeekday } from '../../../../utils/appDateFormat';
import { formatLocalDateParam, apiDateOnlyParam, parseApiDateOnly } from '@/utils/apiDate';

interface CalendarDay {
  date: Date;
  dateString: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  holidays: HolidayView[];
}

interface HolidayCalendarProps {
  holidays: HolidayView[];
  upcomingHolidays: UpcomingHolidayView[];
}

const HolidayCalendar: React.FC<HolidayCalendarProps> = ({ holidays, upcomingHolidays }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<CalendarDay | null>(null);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Sun..Sat in the app language (4 Jan 2026 is a Sunday).
  const weekDays = Array.from({ length: 7 }, (_, i) => appWeekday(new Date(2026, 0, 4 + i), 'short'));

  const calendarData = useMemo((): CalendarDay[] => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

    const endDate = new Date(lastDayOfMonth);
    endDate.setDate(endDate.getDate() + (6 - lastDayOfMonth.getDay()));

    const days: CalendarDay[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const dateString = formatLocalDateParam(current);
      const dayHolidays = holidays.filter(h => {
        const hDate = apiDateOnlyParam(h.date);
        return hDate === dateString;
      });

      days.push({
        date: new Date(current),
        dateString,
        isCurrentMonth: current.getMonth() === currentMonth,
        isToday: current.toDateString() === new Date().toDateString(),
        holidays: dayHolidays,
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentMonth, currentYear, holidays]);

  const navigateMonth = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentMonth + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const handleDateClick = (day: CalendarDay) => {
    if (day.holidays.length > 0) {
      setSelectedDate(day);
    }
  };

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {appMonthNameYear(new Date(currentYear, currentMonth, 1))}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('clickHolidayHint')}
          </p>
        </div>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <button
            onClick={() => navigateMonth(-1)}
            data-testid="leave-holiday-calendar-prev"
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="ChevronLeft" size={16} className="text-muted-foreground rtl:rotate-180" />
          </button>
          <button
            onClick={goToToday}
            data-testid="leave-holiday-calendar-today"
            className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors"
          >
            {t('Today')}
          </button>
          <button
            onClick={() => navigateMonth(1)}
            data-testid="leave-holiday-calendar-next"
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="ChevronRight" size={16} className="text-muted-foreground rtl:rotate-180" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-7 gap-1 mb-3">
          {weekDays.map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarData.map((day, index) => {
            const hasHolidays = day.holidays.length > 0;
            const isSelected = selectedDate?.dateString === day.dateString;

            return (
              <div
                key={index}
                onClick={() => handleDateClick(day)}
                className={`
                  relative p-2 h-12 text-center text-sm transition-all duration-200 rounded-lg
                  ${!day.isCurrentMonth ? 'text-muted-foreground/50' : 'text-foreground'}
                  ${day.isToday ? 'bg-emerald-100 text-emerald-800 font-semibold' : ''}
                  ${hasHolidays ? 'cursor-pointer hover:bg-muted/50' : ''}
                  ${isSelected ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''}
                `}
              >
                <span className="relative z-10">{day.date.getDate()}</span>
                {hasHolidays && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1 rtl:space-x-reverse">
                    {day.holidays.slice(0, 3).map((_, hIndex) => (
                      <div
                        key={hIndex}
                        className="w-1.5 h-1.5 rounded-full bg-green-500"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3">
            {t('Holidays on')} {`${appWeekday(parseApiDateOnly(selectedDate.dateString))}, ${appDate(parseApiDateOnly(selectedDate.dateString))}`}
          </h4>
          <div className="space-y-2">
            {selectedDate.holidays.map((holiday, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-foreground font-medium">{holiday.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Holidays */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="font-medium text-foreground mb-3 flex items-center">
          <Icon name="Clock" size={16} className="ltr:mr-2 rtl:ml-2" />
          {t('Upcoming Holidays')}
        </h4>
        {upcomingHolidays.length > 0 ? (
          <div className="space-y-3">
            {upcomingHolidays.map((holiday, index) => {
              const daysUntil = Math.ceil(
                ((parseApiDateOnly(holiday.date) ?? new Date(NaN)).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
              );

              return (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{holiday.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {`${appWeekday(parseApiDateOnly(holiday.date), 'short')} ${appDayMonth(parseApiDateOnly(holiday.date))}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-end">
                    <p className="text-xs text-muted-foreground">
                      {daysUntil === 0 ? t('Today') : daysUntil === 1 ? t('Tomorrow') : `${daysUntil} ${t('days')}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <Icon name="Calendar" size={32} className="mx-auto mb-2 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">{t('No upcoming holidays')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayCalendar;
